import React from 'react';
import { useNavigate } from 'react-router-dom';

export interface IssueDistribution {
  type: string;
  displayName: string;
  count: number;
  severity?: string;
  affectedFlows?: number;
}

interface IssueDistributionChartProps {
  issueDistribution: IssueDistribution[];
  selectedOrgId: string | null;
}

// Color mapping for issue severity
const SEVERITY_COLORS: Record<string, string> = {
  high: '#EF4444',    // red-500
  medium: '#F59E0B',  // amber-500
  low: '#3B82F6'      // blue-500
};

export function IssueDistributionChart({ issueDistribution, selectedOrgId }: IssueDistributionChartProps) {
  const navigate = useNavigate();
  
  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-lg font-medium text-gray-900 mb-4">Issue Distribution</h3>
      {issueDistribution.length > 0 ? (
        <div>
          <div className="space-y-3">
            {issueDistribution.slice(0, 8).map((issue, index) => (
              <div 
                key={index} 
                className="flex items-center cursor-pointer hover:bg-gray-50 p-1 rounded transition-colors"
                onClick={() => navigate(`/${selectedOrgId}/flows?issueType=${encodeURIComponent(issue.type)}&context=issueDistribution`)}
                title={`View flows with ${issue.displayName}`}
              >
                <div className="flex-1 flex items-center justify-between">
                  <span className="text-sm text-gray-700 truncate pr-4" style={{ minWidth: '200px' }}>
                    {issue.displayName}
                  </span>
                  <div className="flex items-center flex-1">
                    <div className="flex-1 bg-gray-200 rounded-full h-6 mr-3 relative overflow-hidden">
                      <div 
                        className="h-full rounded-full transition-all duration-300"
                        style={{ 
                          width: `${(issue.count / Math.max(...issueDistribution.map(i => i.count))) * 100}%`,
                          backgroundColor: SEVERITY_COLORS[issue.severity || 'low']
                        }}
                      />
                    </div>
                    <span className="text-sm font-medium text-gray-900 w-12 text-right">
                      {issue.count}
                    </span>
                  </div>
                </div>
              </div>
            ))}
            {issueDistribution.length > 8 && (
              <p className="text-xs text-gray-500 text-center pt-2">
                +{issueDistribution.length - 8} more issue types
              </p>
            )}
          </div>
          <div className="mt-4 flex items-center justify-center space-x-6">
            <div className="flex items-center">
              <div className="w-3 h-3 bg-red-500 rounded-full mr-2"></div>
              <span className="text-sm text-gray-600">High Severity</span>
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 bg-amber-500 rounded-full mr-2"></div>
              <span className="text-sm text-gray-600">Medium Severity</span>
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
              <span className="text-sm text-gray-600">Low Severity</span>
            </div>
          </div>
        </div>
      ) : (
        <div className="text-center py-8 text-gray-500">
          <p className="mb-2">No issue data available.</p>
          <p className="text-sm">Issue analysis may still be processing or no issues were found in your flows.</p>
        </div>
      )}
    </div>
  );
}